/** Discriminator for plant/graph deep links in LLM inline citations. */
export type ComponentScope = 'equipment' | 'process_line' | 'tag'

export { inferScopeFromOntologyId } from './plantview-urns'

const CITE_PREFIX = 'cite:'
const FIGURE_CITE_PREFIX = `${CITE_PREFIX}figure:`
const FIGURE_ATTACHMENT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const FIGURE_MARKDOWN_PATTERN = /!\[([^\]]*)\]\((cite:figure:[^)]+)\)/gi

/** Build an inline markdown link target: `cite:equipment:equip:PMP-101`. */
export function buildCiteToken(scope: ComponentScope, id: string): string {
  return `${CITE_PREFIX}${scope}:${id}`
}

export interface ParsedCiteToken {
  scope: ComponentScope
  id: string
}

export type FigureCitationMarkdownPart =
  | { kind: 'markdown'; markdown: string }
  | { kind: 'figure'; attachmentId: string; caption: string }

/**
 * Parse `cite:scope:id` tokens. Returns null when the string is not a valid
 * cite token. IDs may contain colons (e.g. `equip:PMP-101`).
 */
export function parseCiteToken(value: string): ParsedCiteToken | null {
  if (!value.startsWith(CITE_PREFIX)) return null
  const body = value.slice(CITE_PREFIX.length)
  const firstColon = body.indexOf(':')
  if (firstColon <= 0) return null
  const scope = body.slice(0, firstColon)
  const id = body.slice(firstColon + 1)
  if (id.length === 0) return null
  if (scope !== 'equipment' && scope !== 'process_line' && scope !== 'tag') return null
  return { scope, id }
}

/** Parse `cite:figure:<attachment UUID>` tokens. */
export function parseFigureCiteToken(value: string): string | null {
  if (!value.startsWith(FIGURE_CITE_PREFIX)) return null
  const attachmentId = value.slice(FIGURE_CITE_PREFIX.length)
  return FIGURE_ATTACHMENT_ID_PATTERN.test(attachmentId) ? attachmentId : null
}

/** True only for a well-formed figure attachment citation. */
export function isFigureCiteHref(value: string): boolean {
  return parseFigureCiteToken(value) !== null
}

/** Split agent markdown around valid figure image citations. */
export function splitFigureCitations(markdown: string): FigureCitationMarkdownPart[] {
  const parts: FigureCitationMarkdownPart[] = []
  let offset = 0
  FIGURE_MARKDOWN_PATTERN.lastIndex = 0
  for (
    let match = FIGURE_MARKDOWN_PATTERN.exec(markdown);
    match;
    match = FIGURE_MARKDOWN_PATTERN.exec(markdown)
  ) {
    const attachmentId = parseFigureCiteToken(match[2] ?? '')
    if (!attachmentId) continue
    if (match.index > offset) {
      parts.push({ kind: 'markdown', markdown: markdown.slice(offset, match.index) })
    }
    parts.push({
      kind: 'figure',
      caption: match[1] || 'Drawing evidence',
      attachmentId
    })
    offset = match.index + match[0].length
  }
  if (offset < markdown.length || parts.length === 0) {
    parts.push({ kind: 'markdown', markdown: markdown.slice(offset) })
  }
  return parts
}

/** Map Neo4j node labels from resolve API to component scope. */
export function labelToComponentScope(label: string): ComponentScope | null {
  switch (label) {
    case 'Equipment':
      return 'equipment'
    case 'Line':
    case 'ProcessLine':
      return 'process_line'
    case 'Tag':
    case 'ScadaTag':
      return 'tag'
    default:
      return null
  }
}
